import React from 'react'
import CodeBlock from './CodeBlock'

// Compact markdown renderer for task descriptions: fenced code, headings, lists,
// blockquotes, inline `code`, **bold**, and rules. Enough for the canonical docs.
function inline (text, keyBase) {
  const nodes = []
  const re = /(`[^`]+`|\*\*[^*]+\*\*)/g
  let last = 0, m
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    const t = m[0]
    if (t.startsWith('`')) nodes.push(<code key={`${keyBase}-${m.index}`} className="md-ic">{t.slice(1, -1)}</code>)
    else nodes.push(<strong key={`${keyBase}-${m.index}`}>{t.slice(2, -2)}</strong>)
    last = m.index + t.length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

// `annotate` is optional: given a raw markdown line it returns the objective numbers
// that line covers, or null. Rendered as small badges beside headings and bold leads,
// so the doc shows where each objective is taught. Kept as a prop rather than baked in
// because this renderer is also used for screens that have no objectives at all.
function Badges ({ ns }) {
  if (!ns || !ns.length) return null
  return (
    <span className="md-obj">
      {ns.map(n => <span key={n} className="md-obj__n" title={`Learning objective ${n}`}>{n}</span>)}
    </span>
  )
}

export default function Markdown ({ text, annotate }) {
  const lines = (text || '').split('\n')
  const out = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.startsWith('```')) {
      // The info string decides whether this is code or program output — the docs use
      // ```python for code and a bare fence for expected output. It used to be dropped
      // with the rest of the fence line, which is why nothing could be highlighted.
      const lang = line.slice(3).trim()
      const buf = []; i++
      while (i < lines.length && !lines[i].startsWith('```')) { buf.push(lines[i]); i++ }
      i++
      out.push(<CodeBlock key={i} code={buf.join('\n')} lang={lang} />)
      continue
    }
    if (/^#{1,6}\s/.test(line)) {
      const lvl = line.match(/^#+/)[0].length
      const H = `h${Math.min(lvl + 1, 6)}`
      out.push(React.createElement(
        H,
        { key: i, className: 'md-h' },
        inline(line.replace(/^#+\s/, ''), i),
        annotate ? <Badges key="obj" ns={annotate(line)} /> : null
      ))
      i++; continue
    }
    if (/^>\s?/.test(line)) { out.push(<blockquote key={i} className="md-q">{inline(line.replace(/^>\s?/, ''), i)}</blockquote>); i++; continue }
    if (/^\s*[-*]\s+/.test(line)) { out.push(<div key={i} className="md-li">{inline(line.replace(/^\s*[-*]\s+/, ''), i)}</div>); i++; continue }
    if (/^\s*\d+\.\s+/.test(line)) { out.push(<div key={i} className="md-li md-li--n">{inline(line.replace(/^\s*\d+\.\s+/, ''), i)}</div>); i++; continue }
    if (/^---+$/.test(line.trim())) { out.push(<hr key={i} className="md-hr" />); i++; continue }
    if (line.trim() === '') { out.push(<div key={i} className="md-gap" />); i++; continue }
    out.push(
      <p key={i} className="md-p">
        {inline(line, i)}
        {annotate && /^\*\*/.test(line) ? <Badges ns={annotate(line)} /> : null}
      </p>
    ); i++
  }
  return <div className="md">{out}</div>
}
